/**
 * normalizeImageForUpload
 * ------------------------
 * Shared client-side image normalization for upload flows.
 *
 * Goal: let vendors/users pick a normal modern phone photo (HEIC, oversize JPEG,
 * large PNG) and have it upload successfully without manual resizing or format
 * conversion beforehand.
 *
 * Behavior:
 *   1. Accept a File.
 *   2. Validate it's an image (content-type or extension).
 *   3. Decode via createImageBitmap (fast, handles HEIC on iOS Safari 14+).
 *      Fall back to <img> decode for browsers without createImageBitmap.
 *   4. Resize proportionally to a caller-supplied max edge (1024 profile,
 *      2048 portfolio, 1920 message default).
 *   5. Re-encode to JPEG at quality 0.9, stepping down to 0.6 until the blob
 *      fits under the caller-supplied max size (default 5 MB).
 *   6. Return { blob, filename, width, height, originalSize, finalSize, wasResized }.
 *   7. Throw a descriptive Error if decode truly fails.
 *
 * NOTE: This utility is deliberately single-purpose. It does not upload; callers
 * compose the returned Blob into their existing FormData + axios upload flow.
 */

const DEFAULTS = {
  maxEdge: 2048,
  maxBytes: 5 * 1024 * 1024, // 5 MB — mirrors backend config.MAX_FILE_SIZE
  startQuality: 0.9,
  minQuality: 0.6,
  qualityStep: 0.1,
  outputType: 'image/jpeg',
};

// Presets for common upload surfaces. Callers pass the name to avoid re-typing.
export const PRESETS = {
  profile: { maxEdge: 1024, maxBytes: 5 * 1024 * 1024 },
  portfolio: { maxEdge: 2048, maxBytes: 5 * 1024 * 1024 },
  message: { maxEdge: 1920, maxBytes: 10 * 1024 * 1024 },
};

const isImageFile = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  // iOS sometimes hands us HEIC/HEIF with content-type '' — fall back to extension.
  const name = (file.name || '').toLowerCase();
  return /\.(jpe?g|png|gif|webp|heic|heif|bmp|avif)$/i.test(name);
};

const decodeToBitmap = async (file) => {
  // Preferred: createImageBitmap — handles HEIC natively on iOS Safari 14+,
  // and is faster than <img> decode because it's off main thread.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch (e) {
      // Fall through to <img> fallback.
    }
  }
  // Fallback: <img> decode via object URL.
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      // Wrap <img> in a bitmap-like interface so caller code is uniform.
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        _imgEl: img,
        close() { /* no-op for <img> */ },
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image. The file may be corrupted or in an unsupported format.'));
    };
    img.src = url;
  });
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas encode failed.'))),
      type,
      quality,
    );
  });

/**
 * Normalize an image file for upload.
 *
 * @param {File} file - the user-selected File.
 * @param {object} [opts]
 * @param {'profile'|'portfolio'|'message'} [opts.preset='portfolio'] - preset to apply first.
 * @param {number} [opts.maxEdge] - overrides preset.
 * @param {number} [opts.maxBytes] - overrides preset.
 * @returns {Promise<{blob:Blob, filename:string, width:number, height:number, originalSize:number, finalSize:number, wasResized:boolean}>}
 */
export async function normalizeImageForUpload(file, opts = {}) {
  if (!file) {
    throw new Error('No file provided.');
  }
  if (!isImageFile(file)) {
    throw new Error('Please select an image file (JPEG, PNG, HEIC, or WebP).');
  }

  const preset = PRESETS[opts.preset] || PRESETS.portfolio;
  const cfg = { ...DEFAULTS, ...preset, ...opts };

  const originalSize = file.size;
  const bitmap = await decodeToBitmap(file);
  const srcW = bitmap.width;
  const srcH = bitmap.height;
  if (!srcW || !srcH) {
    throw new Error('Could not read image dimensions.');
  }

  // Compute target dimensions that preserve aspect ratio.
  const longest = Math.max(srcW, srcH);
  const scale = longest > cfg.maxEdge ? cfg.maxEdge / longest : 1;
  const targetW = Math.max(1, Math.round(srcW * scale));
  const targetH = Math.max(1, Math.round(srcH * scale));
  const wasResized = scale < 1;

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Browser does not support canvas rendering.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  if (bitmap._imgEl) {
    ctx.drawImage(bitmap._imgEl, 0, 0, targetW, targetH);
  } else {
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  }
  if (typeof bitmap.close === 'function') bitmap.close();

  // Iteratively reduce quality until the blob fits under maxBytes or we hit minQuality.
  let quality = cfg.startQuality;
  let blob = await canvasToBlob(canvas, cfg.outputType, quality);
  while (blob.size > cfg.maxBytes && quality > cfg.minQuality + 1e-6) {
    quality = Math.max(cfg.minQuality, quality - cfg.qualityStep);
    // eslint-disable-next-line no-await-in-loop
    blob = await canvasToBlob(canvas, cfg.outputType, quality);
  }

  // If still over limit at minQuality, try a one-step-smaller edge as a last resort.
  if (blob.size > cfg.maxBytes && cfg.maxEdge > 512) {
    return normalizeImageForUpload(file, { ...opts, maxEdge: Math.round(cfg.maxEdge * 0.75) });
  }

  // Build a friendly filename (always .jpg since we emit JPEG).
  const baseName = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';
  const filename = `${baseName}.jpg`;

  return {
    blob,
    filename,
    width: targetW,
    height: targetH,
    originalSize,
    finalSize: blob.size,
    wasResized,
    quality,
  };
}

/**
 * Convenience: normalize and wrap in a real File so existing FormData code
 * that relies on `file.name` / `file.type` works without edits.
 */
export async function normalizeImageToFile(file, opts = {}) {
  const result = await normalizeImageForUpload(file, opts);
  const normalizedFile = new File([result.blob], result.filename, { type: result.blob.type });
  return { file: normalizedFile, meta: result };
}
